// `#/epo`, `#/epo/...` or `#/epo?...` — never `#/epoch` or other routes that only start with "epo".
const EPO_HASH_PREFIX = /^#\/epo(?=[/?]|$)/;
// MAX's base path: `/marketplace/agentmode` on aws.amazon.com, `/agentmode` on the new-TLD hosts. EPO sits
// beside it under the same prefix.
const AGENT_MODE_PATH = /^(\/marketplace)?\/agentmode\/?$/;
const FRAGMENT_OVERRIDE_PARAM = "awsmpMaxFragmentOverride";

/**
 * The EPO URL for a legacy MAX `#/epo/*` location, or `undefined` when this load must not redirect.
 *
 * Only MAX's two base paths redirect, on any host: `/marketplace/agentmode` → `/marketplace/epo/` and, on
 * the new-TLD `aws.com` hosts, `/agentmode` → `/epo/`. EPO is served next to MAX on the same origin, so the
 * target keeps the current host. Localhost and the Beta and Gamma `*.max.marketplace.aws.dev` hosts serve
 * MAX at `/` and keep MAX's own EPO routes.
 *
 * The hash query (`productId`, `listingId`, `epo_ref`, ...) and the outer query are carried.
 */
export function epoForwardRedirectUrl(location: Pick<Location, "origin" | "pathname" | "hash">, search: URLSearchParams): string | undefined {
  const prefix = EPO_HASH_PREFIX.exec(location.hash);
  const basePath = AGENT_MODE_PATH.exec(location.pathname);
  if (!prefix || !basePath) return undefined;

  const rest = location.hash.slice(prefix[0].length);
  const route = rest.startsWith("/") ? rest : `/${rest}`;
  const query = search.toString();
  const epoBase = `${location.origin}${basePath[1] ?? ""}/epo/`;
  return `${epoBase}${query ? `?${query}` : ""}#${route}`;
}

/** What `initializeApp` does with this load before chrome init and before React mounts. */
export type BootNavigation =
  | { kind: "redirect"; url: string }
  | { kind: "rewrite"; hash: string; search: URLSearchParams }
  | { kind: "stay"; search: URLSearchParams };

/**
 * Applies `awsmpMaxFragmentOverride` (sign-in return URLs carry the route there, double-encoded) and then
 * decides the EPO redirect on the resulting hash. The redirect is decided first so the override never writes
 * a history entry for a load that leaves, and `replace` keeps the legacy URL out of history.
 */
export function resolveBootNavigation(location: Pick<Location, "origin" | "pathname" | "search" | "hash">, redirectEnabled: boolean): BootNavigation {
  const search = new URLSearchParams(location.search);
  const rawOverride = search.get(FRAGMENT_OVERRIDE_PARAM);
  search.delete(FRAGMENT_OVERRIDE_PARAM);
  const overrideHash = rawOverride === null ? undefined : decodeOverride(rawOverride);
  const hash = overrideHash ?? location.hash;

  const url = redirectEnabled ? epoForwardRedirectUrl({ origin: location.origin, pathname: location.pathname, hash }, search) : undefined;
  if (url !== undefined) return { kind: "redirect", url };
  if (overrideHash !== undefined) return { kind: "rewrite", hash, search };
  return { kind: "stay", search };
}

// A malformed override (e.g. a stray `%`) throws URIError, which would stop boot. Ignore it and boot on the
// current hash, as EPO's `restoreHashRouteFromSignIn` does.
function decodeOverride(rawOverride: string): string | undefined {
  try {
    return `#/${decodeURIComponent(decodeURIComponent(rawOverride))}`;
  } catch {
    return undefined;
  }
}
